/**
 * Title screen — full-bleed bg, MNEMOSYNE title, tagline, 5-button vertical menu.
 */

import { hasAutosave } from '../engine/save-manager';
import { AudioEngine } from '../engine/audio';

export interface TitleScreenCallbacks {
  onNewGame: () => void;
  onContinue: () => void;
  onLoad: () => void;
  onSettings: () => void;
  onCredits: () => void;
}

/**
 * Renders the polished title screen into the given root element.
 * Shows full-bleed bg, title, tagline, and a 5-button vertical menu.
 */
export function renderTitleScreen(
  root: HTMLElement,
  _title: string,
  callbacks: TitleScreenCallbacks,
): void {
  root.innerHTML = '';

  const container = document.createElement('div');
  container.id = 'title-screen';

  // Background image (falls back to solid black via CSS)
  const bgImg = document.createElement('img');
  bgImg.className = 'title-bg';
  bgImg.alt = '';
  bgImg.src = './assets/bg/title.webp';
  // If the image fails to load, the CSS gradient background shows instead
  bgImg.addEventListener('error', () => {
    bgImg.style.display = 'none';
  });
  container.appendChild(bgImg);

  const atmosphere = document.createElement('div');
  atmosphere.className = 'title-atmosphere';
  atmosphere.setAttribute('aria-hidden', 'true');

  const shimmer = document.createElement('div');
  shimmer.className = 'title-shimmer';
  atmosphere.appendChild(shimmer);

  const particles = document.createElement('div');
  particles.className = 'title-particles';
  atmosphere.appendChild(particles);

  container.appendChild(atmosphere);

  // Content overlay
  const content = document.createElement('div');
  content.className = 'title-content';

  // Title
  const heading = document.createElement('h1');
  heading.className = 'title-heading';
  heading.textContent = 'THREADBARE';
  content.appendChild(heading);

  // Tagline
  const tagline = document.createElement('p');
  tagline.className = 'title-tagline';
  tagline.textContent = 'Every bond leaves a thread. Not all of them were asked for.';
  content.appendChild(tagline);

  // Reading time estimate
  const eta = document.createElement('p');
  eta.className = 'title-eta';
  eta.textContent = '≈ 1 hour · a visual novel';
  content.appendChild(eta);

  // Menu
  const menu = document.createElement('div');
  menu.className = 'title-menu';

  // New Game
  menu.appendChild(createMenuButton('New Game', callbacks.onNewGame));

  // Continue (disabled if no autosave)
  const continueBtn = createMenuButton('Continue', callbacks.onContinue);
  if (!hasAutosave()) {
    continueBtn.disabled = true;
    continueBtn.classList.add('title-btn-disabled');
  }
  menu.appendChild(continueBtn);

  // Load
  menu.appendChild(createMenuButton('Load', callbacks.onLoad));

  // Settings
  menu.appendChild(createMenuButton('Settings', callbacks.onSettings));

  // Credits
  menu.appendChild(createMenuButton('Credits', callbacks.onCredits));

  content.appendChild(menu);
  container.appendChild(content);

  // ── Title BGM ──────────────────────────────────────────────────────────────
  // Browser blocks audio until the user has interacted with the page.
  // We start the title BGM on the very first mouseenter over the menu (the
  // earliest natural gesture) so it feels instantaneous rather than waiting
  // for a click.  ensureContext() unlocks the AudioContext then playBgm()
  // handles looping + volume from saved settings.
  let titleBgmStarted = false;
  const startTitleBgm = (): void => {
    if (titleBgmStarted) return;
    titleBgmStarted = true;
    const audio = AudioEngine.getInstance();
    audio.ensureContext()
      .then(() => audio.playBgm('./assets/audio/bgm/title.mp3'))
      .catch(() => { /* file may not exist in dev — silent fail */ });
  };
  // Attach to the menu container so any child hover triggers it
  menu.addEventListener('mouseenter', startTitleBgm, { once: true });
  // Also start on first keyboard interaction (Tab to menu)
  menu.addEventListener('focusin', startTitleBgm, { once: true });
  // Also start on any click anywhere on the title screen (most reliable for autoplay policy)
  container.addEventListener('click', startTitleBgm, { once: true });
  container.addEventListener('touchstart', startTitleBgm, { once: true });

  // Esc handler — not needed on title screen, but we set up keyboard nav
  const keyHandler = (e: KeyboardEvent): void => {
    // Allow arrow key navigation between buttons
    const buttons = Array.from(menu.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const focused = document.activeElement as HTMLElement | null;
    const idx = focused ? buttons.indexOf(focused as HTMLButtonElement) : -1;

    if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
      e.preventDefault();
      const next = idx < buttons.length - 1 ? idx + 1 : 0;
      buttons[next]?.focus();
    } else if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
      e.preventDefault();
      const prev = idx > 0 ? idx - 1 : buttons.length - 1;
      buttons[prev]?.focus();
    }
  };
  document.addEventListener('keydown', keyHandler);

  // Store cleanup function on the container so it can be removed
  (container as HTMLDivElement & { __cleanup?: () => void }).__cleanup = () => {
    document.removeEventListener('keydown', keyHandler);
  };

  root.appendChild(container);
}

// Hover SFX debounce timestamp shared across the title menu.
let lastTitleHoverAt = 0;

function createMenuButton(label: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'title-btn';
  btn.textContent = label;
  btn.addEventListener('click', () => {
    // Unlock audio context on first click + play confirm SFX
    void AudioEngine.getInstance().ensureContext().then(() => {
      void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-confirm.mp3');
    });
    onClick();
  });
  // Soft hover click (debounced) — only on enabled buttons
  btn.addEventListener('mouseenter', () => {
    if (btn.disabled) return;
    const now = performance.now();
    if (now - lastTitleHoverAt < 80) return;
    lastTitleHoverAt = now;
    void AudioEngine.getInstance().playSfx('./assets/audio/sfx/ui-click.mp3');
  });
  return btn;
}
